
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { OnboardingState } from "./types";

function deriveStatus(row: any): OnboardingState["status"] {
  if (row?.onboarding_completed_at) return "completed";
  return "in_progress";
}

export function useOnboardingFetch() {
  const { user } = useAuth();
  const [onboarding, setOnboarding] = useState<OnboardingState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    async function fetchState() {
      setLoading(true);
      setError(null);
      if (!user) {
        setOnboarding(null);
        setLoading(false);
        setError("No authenticated user found. Please log in.");
        return;
      }
      try {
        const { data: userProfile, error: userErr } = await supabase
          .from("calie_users")
          .select("tenant_id")
          .eq("user_id", user.id)
          .maybeSingle();

        if (userErr) {
          if (!active) return;
          setOnboarding(null);
          setError(`Error finding your account: ${userErr.message}`);
          setLoading(false);
          return;
        }
        if (!userProfile?.tenant_id) {
          if (!active) return;
          setOnboarding(null);
          setError("Your account is not properly set up. Please contact support.");
          setLoading(false);
          return;
        }
        
        const { data: onboardingData, error: onboardingError } = await supabase
          .from("calie_onboarding_state")
          .select("*")
          .eq("tenant_id", userProfile.tenant_id)
          .maybeSingle();
          
        if (!active) return;
        
        if (onboardingError) {
          setOnboarding(null);
          setError(`Error loading onboarding state: ${onboardingError.message}`);
          setLoading(false);
          return;
        }
        
        const { data: tenantData, error: tenantError } = await supabase
          .from("calie_tenants")
          .select("twilio_sid, twilio_auth_token")
          .eq("tenant_id", userProfile.tenant_id)
          .maybeSingle();
          
        if (!active) return;
        
        if (tenantError) {
          setOnboarding(null);
          setError("Your onboarding setup is incomplete. Please contact support.");
          setLoading(false);
          return;
        }

        if (!onboardingData) {
          setOnboarding(null);
          setError("Your onboarding setup is incomplete. Please contact support.");
          setLoading(false);
          return;
        }

        const hasTwilioAuthToken = !!tenantData?.twilio_auth_token;

        // Always mask token
        setOnboarding({
          ...onboardingData,
          twilio_sid: tenantData?.twilio_sid || null,
          twilio_auth_token: hasTwilioAuthToken ? "**********" : null,
          status: deriveStatus(onboardingData),
        });
        
        setError(null);
      } catch (err) {
        if (!active) return;
        setOnboarding(null);
        setError("An unexpected error occurred. Please try again later.");
      }
      setLoading(false);
    }
    
    fetchState();
    return () => {
      active = false;
    };
  }, [user]);

  return { onboarding, setOnboarding, loading, error };
}
